import type { PdfInvoiceModel, PdfParty } from './model.ts';

export const PDF_ONE_PAGE_OVERFLOW_ERROR =
  'Invoice is too long for one-page PDF. Shorten items or notes.';

export const PDF_ONE_PAGE_LAYOUT = {
  pageHeight: 842,
  topMargin: 44,
  bottomMargin: 44,
  headerHeight: 122,
  partyBlockMinHeight: 84,
  partyTitleHeight: 14,
  partyLineHeight: 12,
  tableHeaderHeight: 18,
  itemRowBaseHeight: 24,
  itemDescriptionLineHeight: 12,
  itemDescriptionCharsPerLine: 50,
  totalsHeight: 100,
  notesHeaderHeight: 16,
  notesLineHeight: 13,
  notesCharsPerLine: 92,
  notesSectionMarginTop: 22,
  sectionGapTotal: 58,
} as const;

export type PdfPreflightResult = { ok: true } | { ok: false; error: string };

export function validatePdfOnePageLayout(model: PdfInvoiceModel): PdfPreflightResult {
  const estimatedHeight =
    PDF_ONE_PAGE_LAYOUT.headerHeight +
    estimatePartySectionHeight(model) +
    PDF_ONE_PAGE_LAYOUT.tableHeaderHeight +
    estimateItemsHeight(model) +
    PDF_ONE_PAGE_LAYOUT.totalsHeight +
    estimateBankDetailsHeight(model.sender.bankRequisites) +
    estimateNotesHeight(model.notes) +
    PDF_ONE_PAGE_LAYOUT.sectionGapTotal;

  return estimatedHeight >= usablePageHeight()
    ? { ok: false, error: PDF_ONE_PAGE_OVERFLOW_ERROR }
    : { ok: true };
}

function usablePageHeight(): number {
  return (
    PDF_ONE_PAGE_LAYOUT.pageHeight -
    PDF_ONE_PAGE_LAYOUT.topMargin -
    PDF_ONE_PAGE_LAYOUT.bottomMargin
  );
}

function estimatePartySectionHeight(model: PdfInvoiceModel): number {
  return Math.max(
    PDF_ONE_PAGE_LAYOUT.partyBlockMinHeight,
    estimatePartyHeight(model.billTo),
    estimatePartyHeight(model.shipTo),
  );
}

function estimatePartyHeight(party: PdfParty | null): number {
  if (!party) return 0;
  const lines =
    1 +
    estimateTextLines(party.email, 40) +
    estimateTextLines(party.billTo, 38) +
    estimateTextLines(party.shipTo, 38);
  return PDF_ONE_PAGE_LAYOUT.partyTitleHeight + lines * PDF_ONE_PAGE_LAYOUT.partyLineHeight;
}

function estimateItemsHeight(model: PdfInvoiceModel): number {
  return model.items.reduce((total, item) => {
    const descriptionLines = Math.max(
      1,
      estimateTextLines(item.description, PDF_ONE_PAGE_LAYOUT.itemDescriptionCharsPerLine),
    );
    const rowHeight =
      PDF_ONE_PAGE_LAYOUT.itemRowBaseHeight +
      (descriptionLines - 1) * PDF_ONE_PAGE_LAYOUT.itemDescriptionLineHeight;
    return total + rowHeight;
  }, 0);
}

function estimateNotesHeight(notes: string): number {
  const lines = estimateTextLines(notes, PDF_ONE_PAGE_LAYOUT.notesCharsPerLine);
  if (lines === 0) return 0;
  return (
    PDF_ONE_PAGE_LAYOUT.notesSectionMarginTop +
    PDF_ONE_PAGE_LAYOUT.notesHeaderHeight +
    lines * PDF_ONE_PAGE_LAYOUT.notesLineHeight
  );
}

// Bank details block uses the same styles as Notes (eyebrow + multiline text
// under the notesSection layout), so its height estimate mirrors estimateNotesHeight.
function estimateBankDetailsHeight(requisites: string): number {
  const lines = estimateTextLines(requisites, PDF_ONE_PAGE_LAYOUT.notesCharsPerLine);
  if (lines === 0) return 0;
  return (
    PDF_ONE_PAGE_LAYOUT.notesSectionMarginTop +
    PDF_ONE_PAGE_LAYOUT.notesHeaderHeight +
    lines * PDF_ONE_PAGE_LAYOUT.notesLineHeight
  );
}

function estimateTextLines(value: string | null, charsPerLine: number): number {
  if (value == null || value.trim() === '') return 0;
  return value
    .trim()
    .split(/\r?\n/)
    .reduce((total, line) => total + Math.max(1, Math.ceil(line.trim().length / charsPerLine)), 0);
}
